import { FC, useMemo, useEffect, useCallback } from 'react'
import { FloatingPane } from 'interfaces/floatingPanes'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { carsGuard, carsMakesGuard } from 'guards/propertyGuards'
import { Guards } from 'interfaces/guards'
import { connectShadedLoadable } from 'helpers/loadableHelpers'
import { toggleCarEditActionCreator } from 'actions/settingsActions'
import { killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { getItemFromCollection } from 'helpers/normalizeHelpers'
import { carToFormValues } from 'helpers/propertyHelpers'
import { CAR_FORM_INITIAL_VALUES } from 'constants/forms'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import CarEditForm from 'components/features/pfm/CarEditForm/CarEditForm'
import { onFormSubmit } from 'helpers/formHelpers'
import { FloatingPaneHeader } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'

const TYPED_PANE = {
  type: 'car-edit' as const,
}

type CarEditPaneProps = {

}

const CarEditPaneComponent: FC<CarEditPaneProps> = (props) => {
  const {} = props

  const paneSelector = useMemo(createFloatingPaneByTypeSelector, [])
  const pane = useStoreSelector((state) => paneSelector(state, TYPED_PANE)) as Extract<FloatingPane, typeof TYPED_PANE>

  const { carId = null } = pane || {}

  return (
    <CarEditPaneInner carId={ carId } key={ `${ carId }` } />
  )
}

type CarEditPaneInnerProps = {
  carId: number | null
}
const CarEditPaneInner: FC<CarEditPaneInnerProps> = (props) => {
  const { carId } = props

  const dispatch = useStoreDispatch()
  useEffect(() => {
    return () => {
      dispatch(toggleCarEditActionCreator(null))
    }
  }, [dispatch])

  const handleClose = useCallback(() => {
    dispatch(killFloatingPanesActionCreator('car-edit'))
  }, [dispatch])

  const editingCar = useStoreSelector((state) => {
    const { cars } = state.data.property
    if (!cars || !carId) {
      return null
    }
    return getItemFromCollection(cars, carId)
  })

  const initialValues = useMemo(() => {
    return editingCar ? carToFormValues(editingCar) : CAR_FORM_INITIAL_VALUES
  }, [editingCar])

  const headerChild = useMemo(() => (
    <FloatingPaneHeader
      title={ !!carId ? 'Edit Your Car' : 'Add a Car' }
      compact
    />
  ), [carId])

  return (
    <FloatingPaneLayout
      type='house-edit'
      header={ headerChild }
      onClose={ handleClose }
      cardLayout
    >
      <CarEditForm
        carId={ carId || -1 }
        onCancel={ handleClose }
        onDelete={ handleClose }
        onUnlink={ handleClose }
        onSubmit={ onFormSubmit }
        initialValues={ initialValues }
      />
    </FloatingPaneLayout>
  )
}

const guards: Guards = [
  carsGuard,
  carsMakesGuard,
]

export const CarEditPane = connectShadedLoadable<{}, {}, {}>(() => () => ({}), undefined, guards)(CarEditPaneComponent)
